"use client";
import React, { createContext, use, useMemo } from "react";
import rosetta, { type Rosetta } from "rosetta";
import { getLang, type Routing } from "../shared";

interface IntlContext {
  messages: Record<string, string>;
  locale: string;
  routing: Routing;
  client: Rosetta<{}>;
}
export const IntlContext = createContext<IntlContext>(null!);

export const IntlProvider = ({
  children,
  messages,
  locale,
  routing,
}: Omit<IntlContext, "client"> & { children: React.ReactNode }) => {
  const client = useMemo(() => {
    const client = rosetta({
      [locale]: messages,
    });

    client.locale(locale);

    return client;
  }, [messages, locale]);

  if (process.env.NODE_ENV === "development") {
    if (!locale) {
      throw new Error(
        "No locale detected, have you passed it into IntlProvider?"
      );
    }

    if (!routing) {
      throw new Error(
        "No locales detected, have you passed them into IntlProvider?"
      );
    }
  }

  return (
    <IntlContext.Provider value={{ messages, locale, client, routing }}>
      {children}
    </IntlContext.Provider>
  );
};

const useIntlContextInternal = (__initiator?: string) => {
  const ctx = use(IntlContext);
  if (!ctx) {
    throw new Error(`${__initiator} must be used within a IntlProvider`);
  }
  return ctx;
};

export const useLocale = () => {
  return useIntlContextInternal("useLocale").locale;
};

export const useTranslations = (scope?: string) => {
  const ctx = useIntlContextInternal("useTranslations");

  return (...args: Parameters<(typeof ctx.client)["t"]>) => {
    const [arg0, ...rest] = args;
    const scoped = scope ? `${scope}.${arg0}` : arg0;
    return ctx.client.t(scoped, ...rest) || scoped.toString();
  };
};

export const useMessages = () => {
  return useIntlContextInternal("useMessages").messages;
};

export const useLocales = () => {
  return useIntlContextInternal("useLocales").routing.locales;
};

/**
 * Returns the language of the current locale
 *
 * @example `en-US`
 * ```tsx
 * const lang = useLanguage();
 * return <p>{lang}</p> // "en"
 * ```
 * @example `en-us`
 * ```tsx
 * const lang = useLanguage();
 * return <p>{lang}</p> // "en"
 * ```
 * @exmaple `en`
 * ```tsx
 * const lang = useLanguage();
 * return <p>{lang}</p> // "en"
 * ```
 */
export const useLang = () => {
  const locale = useLocale();
  return getLang(locale);
};

export const useIntlContext = () => {
  return useIntlContextInternal("useIntlContext");
};

export const useRouting = () => {
  const ctx = useIntlContextInternal("useRouting");
  return ctx.routing;
};
